import { isAbortError } from '@helpers/isAbortError';
import { useAPI } from '@providers/useAPI';
import { useMutation } from '@tanstack/react-query';
import type { AdminCloneJobBodyDto } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { notifyApiCallError } from 'app/http/notifyApiCallError';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { enqueueAndWaitForAdminClone } from '../api/enqueueAndWaitForAdminClone';

export const useAdminCloneJobMutation = (onSuccess: () => void) => {
  const navigate = useNavigate();
  const { call } = useAPI();
  const controllers = useRef(new Set<AbortController>());
  useEffect(
    () => () => {
      controllers.current.forEach((controller) => controller.abort());
    },
    [],
  );
  const mutation = useMutation({
    mutationFn: async (requestBody: AdminCloneJobBodyDto) => {
      const controller = new AbortController();
      controllers.current.add(controller);
      try {
        return await enqueueAndWaitForAdminClone(call, requestBody, {
          signal: controller.signal,
          onError: (message) => toast.error(message),
        });
      } finally {
        controllers.current.delete(controller);
      }
    },
    onSuccess: (clonedJobId) => {
      toast.success('Job cloned successfully');
      onSuccess();
      navigate(`/admin/job-details/${clonedJobId}`);
    },
    onError: (error) => {
      if (isAbortError(error)) {
        return;
      }
      if (error instanceof ApiCallError) {
        notifyApiCallError(error);
      } else {
        toast.error(error.message);
      }
    },
  });
  return {
    ...mutation,
    cancel: () => controllers.current.forEach((controller) => controller.abort()),
  };
};
